/**
 * SecureEmbed Partner Discovery Page
 *
 * Tier 1 - Partner organisation search, cross-sector matching, similarity
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Loader2, AlertCircle, Building2, Globe, MapPin,
  ExternalLink, Sparkles, Users, Tag, ChevronDown, ChevronUp, X, Link2,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { partnerApi, energyOrgApi, PartnerOrg } from '../services/api';
import { RECHARTS_THEME } from '../constants/colors';
import ChartCard from '../components/ChartCard';
import StatCard from '../components/StatCard';
import TierGate from '../components/TierGate';
import RelatedConnections from '../components/RelatedConnections';
import RelatedPagesCard from '../components/RelatedPagesCard';

const SOURCE_LABELS: Record<string, string> = {
  aemo_market: 'AEMO Market Data',
  aemo_generators: 'AEMO Generators',
  aemo_participants: 'AEMO Participants',
  aer_registry: 'AER Registry',
  clean_energy_council: 'Clean Energy Council',
  energy_networks: 'Energy Networks Australia',
  arena_projects: 'ARENA Projects',
  csiro_energy: 'CSIRO Energy',
  university_energy: 'University Energy Research',
  cross_sector: 'Cross-Sector',
  cer_power_stations: 'CER Power Stations',
  cer_agents: 'CER Agents',
  eia_api: 'EIA Power Plants',
  wikidata_sparql: 'Wikidata',
  openalex_api: 'OpenAlex Research',
  opencorporates: 'OpenCorporates',
  gem_coal: 'GEM Coal Tracker',
  gem_gas: 'GEM Gas Tracker',
  gem_wind: 'GEM Wind Tracker',
  gem_solar: 'GEM Solar Tracker',
  world_bank: 'World Bank',
  energy_organisations: 'Energy Organisations',
  competitor_seed: 'Known Competitors',
  grid_connection_queue: 'Grid Connection Queue',
  rez_capacity: 'REZ Capacity',
  ppa_intelligence: 'PPA Intelligence',
  network_constraints: 'Network Constraints',
  planning_approvals: 'Planning Approvals',
  construction_milestones: 'Construction Milestones',
  community_sentiment: 'Community Sentiment',
};

const SAMPLE_QUERIES = [
  'renewable energy project developer',
  'electricity network operator',
  'battery storage technology',
  'solar farm EPC contractor',
  'hydrogen fuel cell research',
  'grid-scale energy storage',
];

const CATEGORIES = [
  'Generation', 'Transmission & Distribution', 'Renewable Energy',
  'Battery & Storage', 'Hydrogen & Green Fuels', 'Energy Trading',
  'Grid Services', 'Energy Efficiency', 'Regulatory & Compliance',
];

const ENERGY_SECTOR_FILTERS = [
  { key: 'Generation', label: 'Generation' },
  { key: 'Renewable Energy', label: 'Renewables' },
  { key: 'Battery & Storage', label: 'Storage' },
  { key: 'Transmission & Distribution', label: 'T&D' },
  { key: 'Hydrogen & Green Fuels', label: 'Hydrogen' },
  { key: 'Energy Trading', label: 'Trading' },
  { key: 'Grid Services', label: 'Grid Services' },
];

// I1: Facility type filter chips
const FACILITY_TYPE_FILTERS = [
  { key: 'Solar Farm', label: 'Solar Farm' },
  { key: 'Wind Farm', label: 'Wind Farm' },
  { key: 'BESS', label: 'BESS' },
  { key: 'Gas Turbine', label: 'Gas Turbine' },
  { key: 'Hydro', label: 'Hydro' },
  { key: 'Coal', label: 'Coal' },
  { key: 'Hybrid', label: 'Hybrid' },
];

const Partners: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PartnerOrg[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryTimeMs, setQueryTimeMs] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [similarOrgs, setSimilarOrgs] = useState<Record<string, PartnerOrg[]>>({});
  const [similarLoading, setSimilarLoading] = useState<string | null>(null);
  const [pieFilterSource, setPieFilterSource] = useState<string | null>(null);

  // Stats
  const [stats, setStats] = useState<{
    total_organisations: number;
    by_source: Record<string, number>;
    by_country: Record<string, number>;
    by_category: Record<string, number>;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [facilityTypeFilter, setFacilityTypeFilter] = useState('');

  // Load stats on mount
  useEffect(() => {
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const resp = await partnerApi.getStats();
        if (resp.success) {
          setStats(resp.data);
        }
      } catch {
        // Stats are non-critical
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const q = searchQuery || query;
    if (!q.trim()) return;

    setIsSearching(true);
    setError(null);
    setResults([]);
    setExpandedOrg(null);
    setSimilarOrgs({});

    try {
      // Search both partner-discovery and energy-organisations in parallel
      const [partnerResp, energyOrgResp] = await Promise.allSettled([
        partnerApi.search(q, {
          source: sourceFilter || undefined,
          country: countryFilter || undefined,
          category: categoryFilter || undefined,
          limit: 20,
        }),
        energyOrgApi.search(q, {
          country: countryFilter || undefined,
          facility_type: facilityTypeFilter || categoryFilter || undefined,
          limit: 20,
        }),
      ]);

      let combined: PartnerOrg[] = [];
      let timeMs: number | null = null;

      if (partnerResp.status === 'fulfilled' && partnerResp.value.success && partnerResp.value.data) {
        combined.push(...(partnerResp.value.data.results || []));
        timeMs = partnerResp.value.data.query_time_ms || null;
      }

      if (energyOrgResp.status === 'fulfilled' && energyOrgResp.value.success && energyOrgResp.value.data) {
        combined.push(...(energyOrgResp.value.data.results || []));
        if (!timeMs) timeMs = energyOrgResp.value.data.query_time_ms || null;
      }

      // Sort by score descending, dedup by name
      const seen = new Set<string>();
      combined = combined
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .filter(org => {
          const key = org.name.toLowerCase().trim();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      setResults(combined);
      setQueryTimeMs(timeMs);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [query, sourceFilter, countryFilter, categoryFilter, facilityTypeFilter]);

  const handleFindSimilar = useCallback(async (orgId: string) => {
    if (similarOrgs[orgId]) return; // Already loaded
    setSimilarLoading(orgId);
    try {
      const resp = await partnerApi.getSimilar(orgId, 5);
      if (resp.success) {
        setSimilarOrgs(prev => ({ ...prev, [orgId]: resp.data.results }));
      }
    } catch {
      // Non-critical
    } finally {
      setSimilarLoading(null);
    }
  }, [similarOrgs]);

  const toggleOrg = useCallback((orgId: string) => {
    setExpandedOrg(prev => prev === orgId ? null : orgId);
  }, []);

  // Prepare chart data from stats
  const sourceChartData = stats ? Object.entries(stats.by_source)
    .map(([name, value]) => ({ name: SOURCE_LABELS[name] || name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8) : [];

  const countryChartData = stats ? Object.entries(stats.by_country)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8) : [];

  const PIE_COLORS = ['#EC4899', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Partner Discovery</h2>
        <p className="text-gray-500 mt-1">
          Search and explore partner organisations across energy generation, storage, and market ecosystems
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Organisations"
          value={statsLoading ? '...' : stats ? (results.length > 0 ? `${results.length} of ${stats.total_organisations.toLocaleString()}` : stats.total_organisations.toLocaleString()) : '--'}
          icon={Building2}
          iconBg="bg-pink-100"
          iconColor="text-pink-600"
        />
        <StatCard
          title="Data Sources"
          value={statsLoading ? '...' : stats ? Object.keys(stats.by_source).length : '--'}
          icon={Globe}
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
        />
        <StatCard
          title="Countries"
          value={statsLoading ? '...' : stats ? Object.keys(stats.by_country).length : '--'}
          icon={MapPin}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Categories"
          value={statsLoading ? '...' : stats ? Object.keys(stats.by_category).length : '--'}
          icon={Tag}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
        />
      </div>

      {/* Energy Sector Filter Chips */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500 self-center mr-1">Sector:</span>
        {ENERGY_SECTOR_FILTERS.map(sf => (
          <button
            key={sf.key}
            onClick={() => setCategoryFilter(categoryFilter === sf.key ? '' : sf.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              categoryFilter === sf.key
                ? 'bg-[#2B064F] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {sf.label}
          </button>
        ))}
      </div>

      {/* I1: Facility Type Filter Chips */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500 self-center mr-1">Facility:</span>
        {FACILITY_TYPE_FILTERS.map(ft => (
          <button
            key={ft.key}
            onClick={() => setFacilityTypeFilter(facilityTypeFilter === ft.key ? '' : ft.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              facilityTypeFilter === ft.key
                ? 'bg-violet-600 text-white'
                : 'bg-violet-50 text-violet-700 hover:bg-violet-100'
            }`}
          >
            {ft.label}
          </button>
        ))}
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search partner organisations by name, capability, or technology..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-sm"
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={isSearching || !query.trim()}
            className="px-6 py-3 bg-[#2B064F] text-white rounded-lg hover:bg-[#3a0a6e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
          >
            {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border rounded-lg text-sm font-medium flex items-center gap-2 ${showFilters ? 'bg-pink-50 border-pink-300 text-pink-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Sources</option>
                {Object.entries(SOURCE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Countries</option>
                {Object.keys(stats?.by_country || {}).sort().map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {(sourceFilter || countryFilter || categoryFilter || facilityTypeFilter) && (
              <div className="md:col-span-3">
                <button
                  onClick={() => { setSourceFilter(''); setCountryFilter(''); setCategoryFilter(''); setFacilityTypeFilter(''); }}
                  className="text-xs text-pink-600 hover:text-pink-700 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Sample Queries */}
        {!results.length && !isSearching && !error && (
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-gray-500">Try:</span>
            {SAMPLE_QUERIES.map((sq) => (
              <button
                key={sq}
                onClick={() => { setQuery(sq); handleSearch(sq); }}
                className="px-3 py-1 bg-pink-50 text-pink-700 rounded-full text-xs hover:bg-pink-100 transition-colors"
              >
                {sq}
              </button>
            ))}
          </div>
        )}

        {/* Search Metadata */}
        {results.length > 0 && (
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
            <span>{results.length} organisations found</span>
            {queryTimeMs && <span>{queryTimeMs}ms</span>}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Loading */}
        {isSearching && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-3 mt-4">
            {results.map((org) => (
              <div
                key={org.id}
                className="border border-gray-200 rounded-lg hover:border-pink-300 transition-colors"
              >
                {/* Org Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleOrg(org.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-pink-500 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{org.name}</h3>
                        {org.score != null && (
                          <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-xs rounded-full font-medium">
                            {(org.score * 100).toFixed(0)}% match
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                        {org.location_country && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {org.location_city ? `${org.location_city}, ${org.location_country}` : org.location_country}
                          </span>
                        )}
                        {org.source && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {SOURCE_LABELS[org.source] || org.source}
                          </span>
                        )}
                        {org.facility_type && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {org.facility_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      {org.url && (
                        <a
                          href={org.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1.5 text-gray-400 hover:text-pink-600 rounded-lg hover:bg-pink-50"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      {expandedOrg === org.id
                        ? <ChevronUp className="w-4 h-4 text-gray-400" />
                        : <ChevronDown className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                  </div>

                  {/* Categories */}
                  {org.categories && org.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {org.categories.map((cat, i) => (
                        <span key={i} className="px-2 py-0.5 bg-violet-50 text-violet-700 text-xs rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {expandedOrg === org.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50/50 space-y-4">
                    {org.description && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Description</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">{org.description}</p>
                      </div>
                    )}
                    {org.capabilities && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Capabilities</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">{org.capabilities}</p>
                      </div>
                    )}
                    {org.source_url && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Source</h4>
                        <a
                          href={org.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-pink-600 hover:text-pink-700 flex items-center gap-1"
                        >
                          {org.source_url} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}

                    {/* Connection Explorer Link */}
                    <button
                      onClick={() => navigate(`/search?tab=connections&q=${encodeURIComponent(org.name)}`)}
                      className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
                    >
                      <Link2 className="w-4 h-4" />
                      Explore cross-domain connections for {org.name}
                    </button>

                    {/* Similar Organisations */}
                    <TierGate requiredTier={1}>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Similar Organisations</h4>
                          {!similarOrgs[org.id] && (
                            <button
                              onClick={() => handleFindSimilar(org.id)}
                              disabled={similarLoading === org.id}
                              className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs hover:bg-pink-200 flex items-center gap-1"
                            >
                              {similarLoading === org.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Sparkles className="w-3 h-3" />
                              }
                              Find Similar
                            </button>
                          )}
                        </div>
                        {similarOrgs[org.id] && (
                          <div className="space-y-2">
                            {similarOrgs[org.id].length === 0 ? (
                              <p className="text-xs text-gray-500">No similar organisations found.</p>
                            ) : (
                              similarOrgs[org.id].map((sim) => (
                                <div key={sim.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Users className="w-4 h-4 text-pink-400 flex-shrink-0" />
                                    <span className="text-sm text-gray-800 truncate">{sim.name}</span>
                                    {sim.location_country && (
                                      <span className="text-xs text-gray-400">{sim.location_country}</span>
                                    )}
                                  </div>
                                  {sim.score != null && (
                                    <span className="text-xs text-pink-600 font-medium flex-shrink-0 ml-2">
                                      {(sim.score * 100).toFixed(0)}%
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </TierGate>

                    {/* Cross-domain connections */}
                    <RelatedConnections
                      query={`${org.name} ${org.capabilities?.split(' ').slice(0, 5).join(' ') || ''}`}
                      maxPerGroup={2}
                      maxGroups={2}
                      startCollapsed={true}
                      label="Cross-Domain Connections"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts Section */}
      {stats && (stats.total_organisations > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sourceChartData.length > 0 && (
            <ChartCard title="Organisations by Source" subtitle="Click a segment to filter organisations">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                      onClick={(_: any, index: number) => {
                        const clicked = sourceChartData[index]?.name;
                        setPieFilterSource(prev => prev === clicked ? null : clicked);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {sourceChartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                          opacity={pieFilterSource && pieFilterSource !== entry.name ? 0.4 : 1}
                          stroke={pieFilterSource === entry.name ? '#2B064F' : undefined}
                          strokeWidth={pieFilterSource === entry.name ? 3 : 0}
                        />
                      ))}
                    </Pie>
                    <Tooltip {...RECHARTS_THEME.tooltip} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Filtered org list when pie segment clicked */}
              {pieFilterSource && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">{pieFilterSource} organisations</h4>
                    <button
                      onClick={() => setPieFilterSource(null)}
                      className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {(() => {
                      // Reverse-lookup the source key from the display name
                      const sourceKey = Object.entries(SOURCE_LABELS).find(([, v]) => v === pieFilterSource)?.[0] || pieFilterSource;
                      const filteredOrgs = results.filter(o => o.source === sourceKey || SOURCE_LABELS[o.source] === pieFilterSource);
                      if (filteredOrgs.length === 0) {
                        return (
                          <p className="text-xs text-gray-400 py-2">
                            Search for organisations to see {pieFilterSource} results, or {
                              <button
                                onClick={() => { setSourceFilter(sourceKey); setQuery(pieFilterSource); handleSearch(pieFilterSource); }}
                                className="text-pink-600 hover:text-pink-700 underline"
                              >search now</button>
                            }
                          </p>
                        );
                      }
                      return filteredOrgs.map(org => (
                        <div key={org.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100 hover:border-pink-200 cursor-pointer transition-colors" onClick={() => { toggleOrg(org.id); }}>
                          <div className="flex items-center gap-2 min-w-0">
                            <Building2 className="w-3.5 h-3.5 text-pink-400 shrink-0" />
                            <span className="text-sm text-gray-800 truncate">{org.name}</span>
                          </div>
                          {org.score != null && (
                            <span className="text-xs text-pink-600 font-medium shrink-0 ml-2">{(org.score * 100).toFixed(0)}%</span>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </ChartCard>
          )}

          {countryChartData.length > 0 && (
            <ChartCard title="Organisations by Country" subtitle="Geographic distribution">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={countryChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {countryChartData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...RECHARTS_THEME.tooltip} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}
        </div>
      )}

      {/* Cross-Sector Matching (Tier 1.5) */}
      <TierGate requiredTier={1.5}>
        <ChartCard
          title="Cross-Sector Matching"
          subtitle="Find organisations with overlapping capabilities across energy generation, storage, and market sectors"
        >
          <p className="text-sm text-gray-500">
            Cross-sector matching uses semantic embeddings to discover organisations whose capabilities
            span multiple technology sectors. Search above to find organisations, then use "Find Similar"
            to discover cross-sector connections.
          </p>
        </ChartCard>
      </TierGate>

      <RelatedPagesCard currentRoute="/partners" />
    </div>
  );
};

export default Partners;
